import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  X,
  MessageCircle,
  Star,
  ClipboardCheck,
  ArrowLeft,
  MoreHorizontal,
  ArrowRight,
} from 'lucide-react';
import styles from './BulkActionBar.module.scss';
import { Button } from 'react-bootstrap';

const BulkActionBar = ({
  selectedCount,
  onClearSelection,
  onMessage,
  onMoveToStage,
  currentStage,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    setShowDropdown(false);
  }, [currentStage]);

  if (selectedCount === 0) return null;

  // Define actions matching ApplicantView pattern
  const reviewAction = {
    label: 'Move to Reviewed',
    mobileLabel: 'Reviewed',
    onClick: () => onMoveToStage('reviewed'),
    icon: <ClipboardCheck size={18} />,
  };

  const shortlistAction = {
    label: 'Move to Shortlisted',
    mobileLabel: 'Shortlisted',
    onClick: () => onMoveToStage('shortlisted'),
    icon: <Star size={18} />,
  };

  const moveToAppliedAction = {
    label: 'Move to Applied',
    onClick: () => onMoveToStage('applied'),
    icon: <ArrowLeft size={16} />,
  };

  const moveToReviewedAction = {
    label: 'Move to Reviewed',
    onClick: () => onMoveToStage('reset-reviewed'),
    icon: <ArrowLeft size={16} />,
  };

  const moveToReviewedDeclinedAction = {
    label: 'Move to Reviewed',
    onClick: () => onMoveToStage('reset-reviewed'),
    icon: <ArrowRight size={16} />,
  };

  const declineAction = {
    label: 'Not a fit',
    onClick: () => onMoveToStage('declined'),
    icon: <X size={16} />,
  };

  // Get primary action and secondary actions based on current stage
  const getActionsForStage = () => {
    switch (currentStage) {
      case 'applied':
        return {
          primary: reviewAction,
          secondary: [shortlistAction, declineAction],
        };
      case 'reviewed':
        return {
          primary: shortlistAction,
          secondary: [moveToAppliedAction, declineAction],
        };
      case 'shortlisted':
        return {
          primary: null,
          secondary: [moveToAppliedAction, moveToReviewedAction, declineAction],
        };
      case 'declined':
        return {
          primary: null,
          secondary: [moveToAppliedAction, moveToReviewedDeclinedAction],
        };
      case 'hired':
        return {
          primary: null,
          secondary: [],
        };
      default:
        return { primary: null, secondary: [] };
    }
  };

  const { primary, secondary } = getActionsForStage();
  const showActions = currentStage !== 'invited' && currentStage !== 'all';
  const hasNoPrimaryAction = !primary;

  return (
    <div
      className={`${styles.bulkActionBarContainer} ${
        hasNoPrimaryAction ? styles.narrow : ''
      }`}
    >
      <div className={styles.bulkActionBar}>
        <div className={styles.content}>
          {/* Selection Count */}
          <div className={styles.selectionInfo}>
            <div className={styles.countBadge}>{selectedCount}</div>
            <span className={styles.selectionText}> selected</span>
          </div>

          {/* Divider - Desktop Only */}
          {showActions && <div className={styles.divider} />}

          {/* Actions */}
          {showActions && (
            <div className={styles.actions}>
              {/* Message Button */}
              <Button onClick={onMessage} className={styles.actionButton}>
                <MessageCircle size={18} className={styles.actionIcon} />
                <span
                  className={`${styles.actionText} ${
                    !hasNoPrimaryAction ? styles.hideOnMobile : ''
                  }`}
                >
                  Message
                </span>
              </Button>

              {/* Primary Action */}
              {primary && (
                <Button
                  bsStyle="primary"
                  onClick={primary.onClick}
                  className={styles.primaryActionButton}
                >
                  {primary.icon}
                  <span
                    className={`${styles.actionText} ${
                      primary.mobileLabel ? styles.hideOnMobile : ''
                    }`}
                  >
                    {primary.label}
                  </span>
                  {primary.mobileLabel && (
                    <span
                      className={`${styles.actionText} ${styles.hideOnDesktop}`}
                    >
                      {primary.mobileLabel}
                    </span>
                  )}
                </Button>
              )}

              {/* Secondary Actions Dropdown */}
              {secondary.length > 0 && (
                <div className={styles.dropdownWrapper}>
                  <button
                    type="button"
                    onClick={() => setShowDropdown(!showDropdown)}
                    className={styles.moreOptionsButton}
                  >
                    <MoreHorizontal size={18} />
                  </button>

                  {showDropdown && (
                    <>
                      <div
                        className={styles.backdrop}
                        onClick={() => setShowDropdown(false)}
                        role="presentation"
                      />

                      <div className={styles.dropdownMenu}>
                        {secondary.map((action, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => {
                              action.onClick();
                              setShowDropdown(false);
                            }}
                            className={styles.dropdownItem}
                          >
                            {action.icon}
                            <span>{action.label}</span>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Clear Selection */}
          <button
            type="button"
            onClick={onClearSelection}
            className={styles.clearButton}
          >
            <X size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

BulkActionBar.propTypes = {
  selectedCount: PropTypes.number.isRequired,
  onClearSelection: PropTypes.func.isRequired,
  onMessage: PropTypes.func.isRequired,
  onMoveToStage: PropTypes.func.isRequired,
  currentStage: PropTypes.string.isRequired,
};

export default BulkActionBar;
